import React, {useEffect, useState} from 'react'
import {Modal, Button, Spinner, Table} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {getClientById} from '../../../../../services/ClientService'

export interface ClientBankRow {
  id: number
  bank_name: string | null
  account_holder_name: string | null
  account_number: string | null
  ifsc_code: string | null
  branch_name: string | null
  branch_address: string | null
  account_type: string | null
  micr_code: string | null
  notes: string | null
}

interface ClientBanksModalProps {
  show: boolean
  onHide: () => void
  leadId: number | null
  clientName?: string
}

const dash = (v: string | null | undefined) => (v != null && String(v).trim() !== '' ? v : '—')

const extractLeadFromResponse = (body: any) => {
  if (!body) return null
  if (body.success === false) return null
  return body?.data?.data ?? body?.data ?? body
}

const ClientBanksModal: React.FC<ClientBanksModalProps> = ({show, onHide, leadId, clientName}) => {
  const [loading, setLoading] = useState(false)
  const [banks, setBanks] = useState<ClientBankRow[]>([])
  const [resolvedName, setResolvedName] = useState<string>('')

  useEffect(() => {
    if (!show || !leadId) {
      return
    }

    const fetchBanks = async () => {
      setLoading(true)
      setBanks([])
      try {
        const response = await getClientById(leadId)
        const body = response.data
        const lead = extractLeadFromResponse(body)

        if (!lead) {
          toast.error(body?.reason || 'Failed to load client banks')
          setResolvedName('')
          return
        }

        setResolvedName(lead.name || '')
        const list = lead.client_banks
        setBanks(Array.isArray(list) ? list : [])
      } catch {
        toast.error('Failed to load bank details')
        setBanks([])
        setResolvedName('')
      } finally {
        setLoading(false)
      }
    }

    fetchBanks()
  }, [show, leadId])

  const titleName = clientName || resolvedName

  return (
    <Modal show={show} onHide={onHide} size='lg' centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          Bank accounts
          {titleName ? (
            <span className='text-muted fw-normal fs-6 ms-2'>— {titleName}</span>
          ) : null}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className='d-flex justify-content-center py-10'>
            <Spinner animation='border' variant='primary' />
          </div>
        ) : banks.length === 0 ? (
          <p className='text-muted mb-0'>No bank records found for this client.</p>
        ) : (
          <div className='table-responsive'>
            <Table striped bordered hover size='sm' className='mb-0'>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Bank</th>
                  <th>Account holder</th>
                  <th>Account no.</th>
                  <th>IFSC</th>
                  <th>Branch</th>
                  <th>Type</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {banks.map((b, idx) => (
                  <tr key={b.id ?? idx}>
                    <td>{idx + 1}</td>
                    <td>{dash(b.bank_name)}</td>
                    <td>{dash(b.account_holder_name)}</td>
                    <td>{dash(b.account_number)}</td>
                    <td>{dash(b.ifsc_code)}</td>
                    <td>{dash(b.branch_name)}</td>
                    <td>{dash(b.account_type)}</td>
                    <td>{dash(b.notes)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='light' onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ClientBanksModal
